import { User } from '@economic/react-app';

export const openNewWindowWithPromise = (url: string, timeout?: number, verifyInterval = 500) => {
    const win = window.open(url, '_blank');

    // lets wait until the window is closed
    return new Promise<boolean>((resolve, reject) => {
        if (!win) {
            reject(new Error('Could not open the window'));
            return;
        }
        win.opener = window;
        Reflect.set(window, 'closeTheWindow', () => {
            closeTheWindow(true);
        });

        // close mitID window, stop the timer, remove the callback
        const closeTheWindow = (result: string | boolean) => {
            clearInterval(timer);
            Reflect.deleteProperty(window, 'closeTheWindow');
            if (!win.closed) {
                win.close();
            }

            // if the result is string then it is an error message, so we should reject the promise
            if (typeof result === 'string') {
                reject(new Error(result));
                return;
            }

            // here later we can call an API to verify if the user was logged in
            resolve(result);
        };

        let duration = 0;
        const timer = setInterval(() => {
            if (win.closed) {
                closeTheWindow(true);
            }
            if (timeout && duration >= timeout) {
                closeTheWindow('Timeout');
            }
            duration += verifyInterval; // not the most accurate way to measure time but enough efficient for this case
        }, verifyInterval);
    });
};

export interface PaymentCardsUrlBuilderConfig {
    parentPage?: 'accounting' | 'expenses'; // eg. accounting or expenses
    origin?: string;
}

export const paymentCardsUrlBuilder = (path: string, { parentPage, origin }: PaymentCardsUrlBuilderConfig = {}): string => {
    const moduleName = 'payment-cards';

    if (parentPage) {
        // eg. /accounting/payment-cards/wallet/overview
        return `/${parentPage}/${moduleName}/${path}`;
    }
    // if we not provided the parent page then we will try to get it from the current URL
    const currentUrl = window.location.href;
    const indexKey = `/${moduleName}/`;
    const index = currentUrl.indexOf(indexKey);

    if (index === -1 && !parentPage) {
        throw new Error('You are not inside the payment-cards module');
    }

    // eg. accounting or expenses
    parentPage = currentUrl
        .slice(0, currentUrl.indexOf(moduleName) - 1)
        .split('/')
        .pop() as 'accounting' | 'expenses';

    if (origin) {
        return `${origin}/${parentPage}/${moduleName}/${path}`;
    }

    // eg. /accounting/payment-cards/wallet/overview
    return `/${parentPage}/${moduleName}/${path}`;
};

export const defaultPaymentCardRoute = (
    user: User,
    agreementNumber: number,
    hasAccessToAccounting: boolean,
    hasAccessToExpenses: boolean
): PaymentCardsUrlBuilderConfig['parentPage'] | '' => {
    if (hasAccessToAccounting && hasAccessToExpenses) {
        return user.isSuperUser && user.agreement.number === agreementNumber ? 'expenses' : 'accounting';
    }

    if (hasAccessToAccounting) {
        return 'accounting';
    }

    if (hasAccessToExpenses) {
        return 'expenses';
    }

    return '';
};
